import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { ImmigrationPOAWizard, IMMIGRATION_POA_STEPS } from '../client/wizards/ImmigrationPOAWizard';
import { AdvisorNotes } from './AdvisorNotes';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import ButtonSpinner from '../../components/ui/ButtonSpinner';

const docTypeLabels = {
  will: 'Last Will & Testament',
  trust: 'Revocable Living Trust',
  immigration_poa: 'Special Power of Attorney for Immigration',
};

const GENERIC_STEPS = {
  will: ['Personal Info', 'Beneficiaries', 'Executor', 'Review'],
  trust: ['Personal Info', 'Trustees', 'Beneficiaries', 'Review'],
};

const IMMIGRATION_STATUS_LABELS = {
  citizen: 'U.S. Citizen',
  lpr: 'Lawful Permanent Resident (Green Card)',
  daca: 'DACA Recipient',
  visa_holder: 'Visa Holder',
  undocumented: 'Undocumented',
};

const TRIGGERING_CONDITION_LABELS = {
  deportation: 'Deportation or Removal Order',
  detention: 'Immigration Detention',
  extended_absence: 'Extended Absence from the Country',
  incapacitation: 'Physical or Mental Incapacitation',
  other: 'Other',
};

const ImmigrationCaseSummary = ({ formData }) => {
  const immigrationStatus = formData?.parentImmigrationStatus
    ? (IMMIGRATION_STATUS_LABELS[formData.parentImmigrationStatus] || formData.parentImmigrationStatus)
    : 'Not provided';
  const childCount =
    Array.isArray(formData?.children) && formData.children.length > 0
      ? `${formData.children.length} ${formData.children.length === 1 ? 'Child' : 'Children'}`
      : 'Not provided';
  const triggeringCondition = formData?.triggeringCondition
    ? (TRIGGERING_CONDITION_LABELS[formData.triggeringCondition] || formData.triggeringCondition)
    : 'Not provided';

  const fields = [
    { label: 'Immigration Status', value: immigrationStatus, icon: FiIcons.FiGlobe },
    { label: 'Minor Children', value: childCount, icon: FiIcons.FiUsers },
    { label: 'Triggering Condition', value: triggeringCondition, icon: FiIcons.FiAlertTriangle },
  ];

  return (
    <div className="rounded-xl p-5 border-2" style={{ backgroundColor: '#0f2044', borderColor: '#d97706' }}>
      <div className="flex items-center gap-2.5 mb-4">
        <div className="p-1.5 rounded-md" style={{ backgroundColor: '#d9770620' }}>
          <SafeIcon icon={FiIcons.FiShield} className="text-amber-400 text-base" />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-amber-400">Case Summary</p>
          <p className="text-white font-semibold text-sm leading-tight">Immigration POA</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {fields.map((field) => (
          <div key={field.label} className="rounded-lg px-4 py-3" style={{ backgroundColor: '#ffffff10' }}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <SafeIcon icon={field.icon} className="text-amber-400 text-xs flex-shrink-0" />
              <p className="text-xs font-semibold text-amber-300 uppercase tracking-wider">{field.label}</p>
            </div>
            <p className={`text-sm font-medium leading-snug ${field.value === 'Not provided' ? 'text-slate-400 italic' : 'text-white'}`}>
              {field.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DocumentReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [client, setClient] = useState(null);
  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [errors] = useState({});
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    loadDoc();
  }, [id, user?.id]);

  const loadDoc = async () => {
    setLoadError(null);
    if (!user?.id) return;
    try {
      const d = await api.getDocumentById(id);
      if (!d) {
        setLoadError('This document could not be found.');
        return;
      }
      const profile = await api.getProfileById(d.user_id);
      const noteRows = await api.getNotesByDocument(id);
      const myNote = noteRows.find((n) => n.advisor_id === user.id)?.note ?? '';
      setDoc({ ...d, advisor_notes: myNote });
      setFormData(d.form_data || {});
      setCurrentStep(d.form_data?.currentStep || 0);
      setClient(profile);
    } catch (err) {
      console.error(err);
      setLoadError('Could not load this document. Please go back and try again.');
      setDoc(null);
      setClient(null);
    }
  };

  const getSteps = (type) => {
    if (type === 'immigration_poa') return IMMIGRATION_POA_STEPS;
    return GENERIC_STEPS[type] || ['Step 1'];
  };

  const handleSaveEdits = async () => {
    setSaving(true);
    try {
      const updated = await api.updateDocumentFormData(id, formData);
      setDoc((prev) => (prev ? { ...prev, form_data: updated.form_data } : prev));
      setIsEditing(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData(doc.form_data || {});
    setIsEditing(false);
  };

  const handleMarkFinalized = async () => {
    setFinalizing(true);
    try {
      const updated = await api.finalizeDocument(id, null, null);
      setDoc((prev) => (prev ? { ...updated, advisor_notes: prev.advisor_notes } : prev));
      setShowConfirmModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setFinalizing(false);
    }
  };

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg p-4">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!doc) return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-slate-500">Loading document...</p>
      </div>
    </div>
  );

  const steps = getSteps(doc.type);
  const isLastStep = currentStep === steps.length - 1;
  const isFinalized = doc.status === 'finalized';
  const isInReview = doc.status === 'in_review';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <button
          onClick={() => navigate('/advisor/dashboard')}
          className="text-slate-500 hover:text-brand-navy flex items-center gap-2 mb-4 text-sm transition-colors"
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} /> Back to Portfolio
        </button>
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h1 className="text-2xl font-bold text-brand-navy">{docTypeLabels[doc.type]}</h1>
            {client && (
              <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
                <SafeIcon icon={FiIcons.FiUser} /> {client.name} &bull; {client.email}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={doc.status} />
            {isInReview && (
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={finalizing}
                className="btn-primary !bg-emerald-600 hover:!bg-emerald-700 text-sm"
              >
                {finalizing ? (
                  <>
                    <ButtonSpinner />
                    Finalizing...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiIcons.FiCheckCircle} /> Mark as Reviewed
                  </>
                )}
              </button>
            )}
            {isFinalized && (
              <span className="flex items-center gap-1.5 text-emerald-700 text-sm font-medium bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg">
                <SafeIcon icon={FiIcons.FiLock} /> Finalized
              </span>
            )}
          </div>
        </div>
      </div>

      {doc.type === 'immigration_poa' && <ImmigrationCaseSummary formData={formData} />}

      <div>
        <div className="flex justify-between mb-2 gap-1">
          {steps.map((step, idx) => (
            <div
              key={idx}
              className={`text-xs font-medium text-center flex-1 truncate px-1 ${
                idx < currentStep ? 'text-emerald-600' : idx === currentStep ? 'text-brand-navy font-bold' : 'text-slate-400'
              }`}
            >
              {step}
            </div>
          ))}
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-gold transition-all duration-500"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
        <p className="text-xs text-slate-400 mt-1 text-right">
          Step {currentStep + 1} of {steps.length}
        </p>
      </div>

      {isEditing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
          <SafeIcon icon={FiIcons.FiEdit3} className="text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800 font-medium flex-1">
            Edit mode — changes will be saved when you click "Save Changes".
          </p>
          <button onClick={handleCancelEdit} className="text-xs text-blue-600 hover:underline">
            Cancel
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8">
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-brand-navy">{steps[currentStep]}</h2>
          {!isFinalized && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-navy border border-slate-300 hover:border-brand-navy px-3 py-1.5 rounded-md transition-colors"
            >
              <SafeIcon icon={FiIcons.FiEdit2} /> Edit
            </button>
          )}
          {isEditing && (
            <button
              onClick={handleSaveEdits}
              disabled={saving}
              className="btn-primary text-sm py-1.5 min-w-[120px]"
            >
              {saving ? (
                <>
                  <ButtonSpinner />
                  Saving...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiIcons.FiSave} /> Save Changes
                </>
              )}
            </button>
          )}
        </div>

        {doc.type === 'immigration_poa' ? (
          <ImmigrationPOAWizard
            formData={formData}
            onChange={isEditing ? setFormData : () => {}}
            currentStep={currentStep}
            errors={errors}
          />
        ) : (
          <div className="space-y-3">
            {Object.entries(doc.form_data || {})
              .filter(([k]) => k !== 'currentStep')
              .map(([key, val]) => (
                <div key={key} className="flex justify-between py-2 border-b border-slate-100 last:border-0">
                  <span className="text-sm text-slate-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                  <span className="text-sm font-medium text-slate-800 text-right max-w-[60%]">{String(val)}</span>
                </div>
              ))}
          </div>
        )}

        {doc.type === 'immigration_poa' && (
          <div className="mt-6 pt-4 border-t border-slate-100 flex justify-between">
            <button
              onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
              disabled={currentStep === 0}
              className="btn-secondary text-sm"
            >
              <SafeIcon icon={FiIcons.FiChevronLeft} /> Previous
            </button>
            <button
              onClick={() => setCurrentStep((s) => Math.min(steps.length - 1, s + 1))}
              disabled={isLastStep}
              className="btn-secondary text-sm"
            >
              Next <SafeIcon icon={FiIcons.FiChevronRight} />
            </button>
          </div>
        )}
      </div>

      <AdvisorNotes docId={id} initialNotes={doc.advisor_notes || ''} />

      {showConfirmModal && (
        <ConfirmModal
          title="Mark as Reviewed & Finalized"
          description="This will change the document status from 'In Review' to 'Finalized'. The client will be notified that their document is complete. This action cannot be undone."
          confirmLabel={finalizing ? 'Finalizing...' : 'Yes, Finalize'}
          confirmClassName="btn-primary !bg-emerald-600 hover:!bg-emerald-700"
          onConfirm={handleMarkFinalized}
          onCancel={() => setShowConfirmModal(false)}
          icon={FiIcons.FiCheckCircle}
          iconClassName="text-emerald-500"
        />
      )}
    </div>
  );
};